import { Injectable } from '@angular/core';
import { ToastService } from './toast.service';
import { errorMesssage } from '@shared/constants';

export enum ResponseStatus {
    SUCCESS,
    FAIL
}

export interface InternalResponse {
    status?: ResponseStatus; // 0-success, 1-error
    error?: ErrorPattern;
    data?: any;
}

export interface ErrorPattern {
    code?: string;
    text?: string;
    detail?: any;
}

@Injectable({
    providedIn: 'root'
})
export class ErrorHandlerService {
    constructor(private toastService: ToastService) { }

    handleInternal(response: InternalResponse): { hasError: boolean; data: any } {
        if (response.status == ResponseStatus.FAIL) {
            this.toastService.showError(errorMesssage(response?.error?.code || 'UNKNOWN_ERROR'));
            return { hasError: true, data: null };
        }
        return { hasError: false, data: response.data };
    }

    /**
     * Batch version:
     * - Shows ONE error toast, unified from all failed calls (including thrown errors)
     * - Returns a map of data by key for successful calls
     * - Never throws
     */
    handleInternalBatch(resultsByKey: Record<string, { ok: boolean; response?: InternalResponse; error?: any }>): { hasAnyError: boolean; dataByKey: Record<string, any> } {
        const dataByKey: Record<string, any> = {};
        const errorCodes: string[] = [];

        for (const [key, result] of Object.entries(resultsByKey)) {
            if (result.ok) {
                const resp = result.response as InternalResponse;
                if (resp?.status === ResponseStatus.FAIL) {
                    errorCodes.push(resp?.error?.code || `UNKNOWN_ERROR:${key}`);
                    // do NOT show per-call toast; we’ll show a single unified one below
                } else {
                    dataByKey[key] = resp?.data;
                }
            } else {
                // Thrown/transport error — you can map to a code or attach details
                errorCodes.push(`NETWORK_ERROR:${key}`);
            }
        }

        const hasAnyError = errorCodes.length > 0;
        if (hasAnyError) {
            // Build a single readable line; customize formatting as you like
            const uniqueCodes = Array.from(new Set(errorCodes));
            const unifiedDetail = uniqueCodes.map(code => errorMesssage(code)).join(' • ');
            this.toastService.showError(unifiedDetail);
        }

        return { hasAnyError, dataByKey };
    }
}
